import { useEffect, useState, type CSSProperties } from 'react';
import type { FormFolder } from '@jfh/intake-shared';
import { Icon } from './Icon.js';
import { Modal } from './Modal.js';
import { CardMenu, MenuItem } from './CardMenu.js';
import { useDropTarget, type dragSource } from '../lib/dnd.js';
import {
  TOP_LEVEL_LABEL,
  canMoveTo,
  flattenTree,
  type MoveItem,
} from '../lib/folders.js';

const dropHighlight: CSSProperties = {
  outline: '2px dashed var(--accent)',
  outlineOffset: 2,
  background: 'var(--accent-soft)',
};

// ---------------------------------------------------------------------------
// Breadcrumbs
// ---------------------------------------------------------------------------

export function Breadcrumbs({
  path,
  onOpen,
  dropEnabled,
  onDropTo,
}: {
  path: FormFolder[];
  onOpen: (id: string | null) => void;
  dropEnabled: (target: string | null) => boolean;
  onDropTo: (target: string | null) => void;
}) {
  const crumbs: { id: string | null; name: string }[] = [
    { id: null, name: TOP_LEVEL_LABEL },
    ...path.map((f) => ({ id: f.id, name: f.name })),
  ];
  return (
    <nav aria-label="Folder path" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, minWidth: 0 }}>
      {crumbs.map((c, i) => (
        <span key={c.id ?? 'top'} style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          {i > 0 && <Icon name="chevronR" size={14} style={{ color: 'var(--text-3)', flex: 'none' }} />}
          <Crumb
            name={c.name}
            current={i === crumbs.length - 1}
            onOpen={() => onOpen(c.id)}
            dropEnabled={dropEnabled(c.id)}
            onDrop={() => onDropTo(c.id)}
          />
        </span>
      ))}
    </nav>
  );
}

function Crumb({
  name,
  current,
  onOpen,
  dropEnabled,
  onDrop,
}: {
  name: string;
  current: boolean;
  onOpen: () => void;
  dropEnabled: boolean;
  onDrop: () => void;
}) {
  const { over, props } = useDropTarget(dropEnabled, onDrop);
  return (
    <button
      {...props}
      onClick={onOpen}
      aria-current={current ? 'page' : undefined}
      style={{
        border: 'none',
        background: 'transparent',
        padding: '4px 7px',
        borderRadius: 6,
        cursor: current ? 'default' : 'pointer',
        fontSize: 13.5,
        fontWeight: current ? 700 : 500,
        color: current ? 'var(--text-1)' : 'var(--text-2)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 260,
        ...(over ? dropHighlight : {}),
      }}
    >
      {name}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Folder row (the Forms table, F10 P4)
// ---------------------------------------------------------------------------

export function FolderRow({
  folder,
  path,
  counts,
  onOpen,
  onRename,
  onMove,
  onDelete,
  drag,
  dropEnabled,
  onDrop,
  dimmed,
}: {
  folder: FormFolder;
  /** Shown under the name in search results. */
  path?: string;
  counts: { forms: number; folders: number };
  onOpen: () => void;
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
  drag: ReturnType<typeof dragSource>;
  dropEnabled: boolean;
  onDrop: () => void;
  dimmed?: boolean;
}) {
  const { over, props } = useDropTarget(dropEnabled, onDrop);
  const summary = [
    `${counts.forms} form${counts.forms === 1 ? '' : 's'}`,
    counts.folders ? `${counts.folders} folder${counts.folders === 1 ? '' : 's'}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      {...drag}
      {...props}
      role="button"
      tabIndex={0}
      aria-label={`Open folder ${folder.name}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) onOpen();
      }}
      className="forms-table-row"
      style={{ opacity: dimmed ? 0.45 : 1, ...(over ? dropHighlight : {}) }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <span className="forms-table-icon" style={{ background: 'var(--bg-sunken)', color: 'var(--text-2)' }}>
          <Icon name="folder" size={18} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {folder.name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {path ?? summary}
          </div>
        </div>
      </div>
      <span className="forms-table-mid" />
      <span className="forms-table-mid" />
      <span className="forms-table-mid" />
      <span className="forms-table-changed" />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <CardMenu label={`Actions for folder ${folder.name}`}>
          {(close) => (
            <>
              <MenuItem icon="pencil" label="Rename" onClick={() => { close(); onRename(); }} />
              <MenuItem icon="folderMove" label="Move to…" onClick={() => { close(); onMove(); }} />
              <MenuItem icon="trash" label="Delete" danger onClick={() => { close(); onDelete(); }} />
            </>
          )}
        </CardMenu>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modals
// ---------------------------------------------------------------------------

/** Create or rename a folder. `onSave` throws to show its message inline. */
export function FolderNameModal({
  open,
  title,
  initialName,
  where,
  submitLabel,
  onClose,
  onSave,
}: {
  open: boolean;
  title: string;
  initialName: string;
  /** e.g. "In Customer accounts". */
  where?: string;
  submitLabel: string;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState(initialName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setBusy(false);
      setError(null);
    }
  }, [open, initialName]);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(name.trim());
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} width={420}>
      <div style={{ padding: 22 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>{title}</h3>
        {where && <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 0 }}>{where}</p>}
        <label className="field-label" htmlFor="folder-name">Folder name</label>
        <input
          id="folder-name"
          className="input"
          value={name}
          autoFocus
          maxLength={80}
          placeholder="e.g. Customer accounts"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
          }}
        />
        {error && <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--st-declined)' }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!name.trim() || busy} onClick={() => void submit()}>
            {busy ? 'Saving…' : submitLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** "Move to…" — pick a destination folder from the tree. */
export function MoveModal({
  item,
  folders,
  index,
  onClose,
  onMove,
}: {
  item: MoveItem | null;
  folders: FormFolder[];
  index: Map<string, FormFolder>;
  onClose: () => void;
  onMove: (item: MoveItem, target: string | null) => Promise<void>;
}) {
  const [target, setTarget] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTarget(undefined);
    setBusy(false);
  }, [item]);

  if (!item) return null;
  const rows: { id: string | null; name: string; depth: number }[] = [
    { id: null, name: TOP_LEVEL_LABEL, depth: 0 },
    ...flattenTree(folders).map((r) => ({ id: r.folder.id, name: r.folder.name, depth: r.depth + 1 })),
  ];

  const submit = async () => {
    if (target === undefined || busy) return;
    setBusy(true);
    try {
      await onMove(item, target);
      onClose();
    } catch {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} width={460}>
      <div style={{ padding: 22 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Move “{item.name}”</h3>
        <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 0 }}>Choose where it should go.</p>
        <div
          role="listbox"
          aria-label="Destination folder"
          style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            maxHeight: 320,
            overflowY: 'auto',
            padding: 4,
          }}
        >
          {rows.map((r) => {
            const isCurrent = r.id === item.parentId;
            const allowed = canMoveTo(index, item, r.id);
            const selected = target === r.id;
            return (
              <button
                key={r.id ?? 'top'}
                role="option"
                aria-selected={selected}
                disabled={!allowed}
                onClick={() => setTarget(r.id)}
                onDoubleClick={() => allowed && void onMove(item, r.id).then(onClose, () => undefined)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  border: 'none',
                  borderRadius: 6,
                  padding: '7px 10px',
                  paddingLeft: 10 + r.depth * 18,
                  textAlign: 'left',
                  fontSize: 13.5,
                  fontWeight: r.id === null ? 700 : 500,
                  cursor: allowed ? 'pointer' : 'not-allowed',
                  background: selected ? 'var(--accent-soft)' : 'transparent',
                  color: allowed ? (selected ? 'var(--accent-text)' : 'var(--text-1)') : 'var(--text-3)',
                }}
              >
                <Icon name={r.id === null ? 'forms' : 'folder'} size={15} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                {isCurrent && <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-3)' }}>Current</span>}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={target === undefined || busy} onClick={() => void submit()}>
            {busy ? 'Moving…' : 'Move here'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
